import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { AdminAuthProvider } from "./contexts/AdminAuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import GuestRoute from "./components/GuestRoute";
import DomainGuard from "./components/DomainGuard";

// Buyer-critical pages — loaded eagerly for instant render
import LinkPurchase from "./pages/LinkPurchase";
import PaymentSuccess from "./pages/PaymentSuccess";

// Everything else — lazy-loaded (only fetched when navigated to)
const Home = lazy(() => import("./pages/Home"));
const Signup = lazy(() => import("./pages/Signup"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const CreateOrganization = lazy(() => import("./pages/CreateOrganization"));
const CreateCreator = lazy(() => import("./pages/CreateCreator"));
const CreatorProfile = lazy(() => import("./pages/CreatorProfile"));
const CreatorLinks = lazy(() => import("./pages/CreatorLinks"));
const CreatorAnalytics = lazy(() => import("./pages/CreatorAnalytics"));
const CreatorCustomers = lazy(() => import("./pages/CreatorCustomers"));
const CreatorTransactions = lazy(() => import("./pages/CreatorTransactions"));
const Wallet = lazy(() => import("./pages/Wallet"));
const OrganizationDashboard = lazy(() => import("./pages/OrganizationDashboard"));
const PayoutSettings = lazy(() => import("./pages/PayoutSettings"));
const OrganizationSettings = lazy(() => import("./pages/OrganizationSettings"));
const CreateLink = lazy(() => import("./pages/CreateLink"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const Contact = lazy(() => import("./pages/Contact"));
const Support = lazy(() => import("./pages/Support"));
const LegalCenter = lazy(() => import("./pages/LegalCenter"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const AffiliateTerms = lazy(() => import("./pages/AffiliateTerms"));
const UsagePolicy = lazy(() => import("./pages/UsagePolicy"));
const DMCATakedown = lazy(() => import("./pages/DMCATakedown"));
const ComplaintsRemoval = lazy(() => import("./pages/ComplaintsRemoval"));
const ContentProviderAgreement = lazy(() => import("./pages/ContentProviderAgreement"));
const DocsLayout = lazy(() => import("./pages/docs/DocsLayout"));
const DocsHome = lazy(() => import("./pages/docs/DocsHome"));
const DocsCollection = lazy(() => import("./pages/docs/DocsCollection"));
const DocsArticle = lazy(() => import("./pages/docs/DocsArticle"));
const FloatingChat = lazy(() => import("./components/FloatingChat"));
const CreatorPublicProfile = lazy(() => import("./pages/CreatorPublicProfile"));

// Minimal fallback for lazy-loaded chunks
const LazyFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-black">
    <div className="w-5 h-5 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
  </div>
);

function App() {
  const isUnlockDomain = window.location.hostname === 'unlocky.io' || window.location.hostname === 'www.unlocky.io';

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AdminAuthProvider>
          <DomainGuard>
          <Routes>
            {/* Buyer-critical routes — NO Suspense wrapper, loaded eagerly */}
            <Route path="/l/:shortLink" element={<LinkPurchase />} />
            <Route path="/purchase/success" element={<PaymentSuccess />} />
            <Route path="/c/:creatorId" element={<Suspense fallback={<LazyFallback />}><CreatorPublicProfile /></Suspense>} />

            {/* All other routes — lazy-loaded with Suspense */}
            <Route path="/" element={<Suspense fallback={<LazyFallback />}><Home /></Suspense>} />
            <Route path="/signup" element={<Suspense fallback={<LazyFallback />}><GuestRoute><Signup /></GuestRoute></Suspense>} />
            <Route path="/login" element={<Suspense fallback={<LazyFallback />}><GuestRoute><Login /></GuestRoute></Suspense>} />
            <Route path="/forgot-password" element={<Suspense fallback={<LazyFallback />}><GuestRoute><ForgotPassword /></GuestRoute></Suspense>} />
            <Route path="/verify-email" element={<Suspense fallback={<LazyFallback />}><VerifyEmail /></Suspense>} />
            
            {/* Protected Routes */}
            <Route 
              path="/create-organization" 
              element={
                <ProtectedRoute>
                  <Suspense fallback={<LazyFallback />}><CreateOrganization /></Suspense>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/create-creator" 
              element={
                <ProtectedRoute>
                  <Suspense fallback={<LazyFallback />}><CreateCreator /></Suspense>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/creator/:creatorId/dashboard" 
              element={
                <ProtectedRoute>
                  <Suspense fallback={<LazyFallback />}><Dashboard /></Suspense>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/creator/:creatorId/links" 
              element={
                <ProtectedRoute>
                  <Suspense fallback={<LazyFallback />}><CreatorLinks /></Suspense>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/creator/:creatorId/profile" 
              element={
                <ProtectedRoute>
                  <Suspense fallback={<LazyFallback />}><CreatorProfile /></Suspense>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/creator/:creatorId/analytics" 
              element={
                <ProtectedRoute>
                  <Suspense fallback={<LazyFallback />}><CreatorAnalytics /></Suspense>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/creator/:creatorId/customers" 
              element={
                <ProtectedRoute>
                  <Suspense fallback={<LazyFallback />}><CreatorCustomers /></Suspense>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/creator/:creatorId/transactions" 
              element={
                <ProtectedRoute>
                  <Suspense fallback={<LazyFallback />}><CreatorTransactions /></Suspense>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/wallet" 
              element={
                <ProtectedRoute>
                  <Suspense fallback={<LazyFallback />}><Wallet /></Suspense>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/organization/dashboard" 
              element={
                <ProtectedRoute>
                  <Suspense fallback={<LazyFallback />}><OrganizationDashboard /></Suspense>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/payout-settings" 
              element={
                <ProtectedRoute>
                  <Suspense fallback={<LazyFallback />}><PayoutSettings /></Suspense>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/organization/settings" 
              element={
                <ProtectedRoute>
                  <Suspense fallback={<LazyFallback />}><OrganizationSettings /></Suspense>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/creator/:creatorId/links/create" 
              element={
                <ProtectedRoute>
                  <Suspense fallback={<LazyFallback />}><CreateLink /></Suspense>
                </ProtectedRoute>
              } 
            />

            {/* Public Pages */}
            <Route path="/pricing" element={<Suspense fallback={<LazyFallback />}><PricingPage /></Suspense>} />
            <Route path="/contact" element={<Suspense fallback={<LazyFallback />}><Contact /></Suspense>} />
            <Route path="/support" element={<Suspense fallback={<LazyFallback />}><Support /></Suspense>} />
            <Route path="/legal" element={<Suspense fallback={<LazyFallback />}><LegalCenter /></Suspense>} />
            <Route path="/privacy-policy" element={<Suspense fallback={<LazyFallback />}><PrivacyPolicy /></Suspense>} />
            <Route path="/terms-of-service" element={<Suspense fallback={<LazyFallback />}><TermsOfService /></Suspense>} />
            <Route path="/refund-policy" element={<Suspense fallback={<LazyFallback />}><RefundPolicy /></Suspense>} />
            <Route path="/affiliate-terms" element={<Suspense fallback={<LazyFallback />}><AffiliateTerms /></Suspense>} />
            <Route path="/usage-policy" element={<Suspense fallback={<LazyFallback />}><UsagePolicy /></Suspense>} />
            <Route path="/dmca-takedown" element={<Suspense fallback={<LazyFallback />}><DMCATakedown /></Suspense>} />
            <Route path="/complaints-removal" element={<Suspense fallback={<LazyFallback />}><ComplaintsRemoval /></Suspense>} />
            <Route path="/content-provider-agreement" element={<Suspense fallback={<LazyFallback />}><ContentProviderAgreement /></Suspense>} />

            {/* Docs Routes */}
            <Route path="/docs" element={<Suspense fallback={<LazyFallback />}><DocsLayout><DocsHome /></DocsLayout></Suspense>} />
            <Route path="/docs/:categoryId" element={<Suspense fallback={<LazyFallback />}><DocsLayout><DocsCollection /></DocsLayout></Suspense>} />
            <Route path="/docs/:categoryId/:articleId" element={<Suspense fallback={<LazyFallback />}><DocsLayout><DocsArticle /></DocsLayout></Suspense>} />
          </Routes>
          </DomainGuard>
          </AdminAuthProvider>
        </AuthProvider>
      </BrowserRouter>
      {!isUnlockDomain && (
        <Suspense fallback={null}>
          <FloatingChat />
        </Suspense>
      )}
      <Toaster />
    </div>
  );
}

export default App;
